import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';

import { companiesApi } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { ConfirmDialog } from '../../components/common';

interface Company {
  id: string;
  name: string;
  code: string;
  isActive: boolean;
  createdAt: string;
  _count?: {
    devices: number;
    users: number;
    clients: number;
  };
}

interface CompanyFormData {
  name: string;
  code: string;
  isActive: boolean;
  // Optional: bootstrap the tenant ADMIN at creation time.
  adminEmail: string;
  adminName: string;
}

interface AdminInvite {
  email: string;
  inviteToken: string;
  expiresAt: string;
}

export function CompaniesPage() {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Company | null>(null);
  // Shown once after creating a company with an admin: the one-time invite token.
  const [adminInvite, setAdminInvite] = useState<AdminInvite | null>(null);
  const [formData, setFormData] = useState<CompanyFormData>({
    name: '',
    code: '',
    isActive: true,
    adminEmail: '',
    adminName: '',
  });

  // Tenant management is platform-only (SUPERADMIN).
  const isAdmin = user?.role === 'SUPERADMIN';

  const { data, isLoading } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const response = await companiesApi.list({ limit: 100 });
      return response.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => companiesApi.create(payload),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      // If a tenant ADMIN was bootstrapped, surface its one-time invite token.
      const created = res?.data?.data as { admin?: AdminInvite } | undefined;
      closeModal();
      if (created?.admin?.inviteToken) {
        setAdminInvite(created.admin);
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CompanyFormData> }) =>
      companiesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      closeModal();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => companiesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
    },
  });

  const companies = (data?.data || []) as Company[];

  const openModal = (company?: Company) => {
    if (company) {
      setEditingCompany(company);
      setFormData({
        name: company.name,
        code: company.code,
        isActive: company.isActive,
        adminEmail: '',
        adminName: '',
      });
    } else {
      setEditingCompany(null);
      setFormData({ name: '', code: '', isActive: true, adminEmail: '', adminName: '' });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingCompany(null);
    setFormData({ name: '', code: '', isActive: true, adminEmail: '', adminName: '' });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingCompany) {
      updateMutation.mutate({
        id: editingCompany.id,
        data: { name: formData.name, code: formData.code, isActive: formData.isActive },
      });
    } else {
      // Only include the admin block when an email was provided.
      const payload: Record<string, unknown> = { name: formData.name, code: formData.code };
      if (formData.adminEmail.trim()) {
        payload.admin = {
          email: formData.adminEmail.trim(),
          name: formData.adminName.trim() || formData.adminEmail.trim(),
        };
      }
      createMutation.mutate(payload);
    }
  };

  const confirmDelete = () => {
    if (deleteTarget) {
      deleteMutation.mutate(deleteTarget.id);
      setDeleteTarget(null);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Empresas</h1>
          <p className="text-gray-600">Gestión de empresas del sistema</p>
        </div>
        {isAdmin && (
          <button
            onClick={() => openModal()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Nueva Empresa
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="text-center py-10">Cargando...</div>
      ) : companies.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-6">
          <p className="text-gray-500 text-center py-10">
            No hay empresas registradas
          </p>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Código
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nombre
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dispositivos
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usuarios
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Creado
                </th>
                {isAdmin && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {companies.map((company) => (
                <tr key={company.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                      {company.code}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-medium text-gray-900">
                      {company.name}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {company._count?.devices || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {company._count?.users || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        company.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {company.isActive ? 'Activa' : 'Inactiva'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {format(new Date(company.createdAt), 'dd/MM/yyyy')}
                  </td>
                  {isAdmin && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => openModal(company)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => setDeleteTarget(company)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Eliminar
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Invitación del ADMIN recién creado — se muestra UNA sola vez. */}
      {adminInvite && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h2 className="text-xl font-bold mb-2">Administrador invitado</h2>
            <p className="text-sm text-gray-600 mb-4">
              Comparte este enlace/token con <strong>{adminInvite.email}</strong> para que
              active su cuenta. <strong>Se muestra una sola vez.</strong>
            </p>
            <label className="block text-xs font-medium text-gray-500 mb-1">Invite token</label>
            <div className="flex gap-2 mb-4">
              <input
                readOnly
                value={adminInvite.inviteToken}
                className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-xs bg-gray-50"
                onFocus={(e) => e.target.select()}
              />
              <button
                type="button"
                onClick={() => navigator.clipboard?.writeText(adminInvite.inviteToken)}
                className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
              >
                Copiar
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              Activa con <code>POST /auth/accept-invite</code> (token + nueva contraseña).
              Caduca el {format(new Date(adminInvite.expiresAt), 'dd/MM/yyyy')}.
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => setAdminInvite(null)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Listo
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Eliminar empresa"
        message={`Esta seguro de eliminar la empresa "${deleteTarget?.name}"? Esta accion no se puede deshacer.`}
        confirmLabel="Eliminar"
        destructive
        isLoading={deleteMutation.isPending}
        onConfirm={confirmDelete}
        onClose={() => setDeleteTarget(null)}
      />

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {editingCompany ? 'Editar Empresa' : 'Nueva Empresa'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Código
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) =>
                    setFormData({ ...formData, code: e.target.value.toUpperCase() })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">Activa</span>
                </label>
              </div>

              {/* Admin del tenant: solo al crear. Opcional — deja el tenant listo
                  para que su dueño entre al portal (se invita por token). */}
              {!editingCompany && (
                <div className="mb-4 border-t pt-4">
                  <p className="text-sm font-semibold text-gray-800 mb-1">
                    Administrador del tenant <span className="font-normal text-gray-500">(opcional)</span>
                  </p>
                  <p className="text-xs text-gray-500 mb-2">
                    Crea el ADMIN dueño de esta empresa. Se le generará un enlace de invitación
                    para que defina su contraseña.
                  </p>
                  <input
                    type="email"
                    placeholder="Email del administrador"
                    value={formData.adminEmail}
                    onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })}
                    className="w-full px-3 py-2 mb-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="text"
                    placeholder="Nombre del administrador"
                    value={formData.adminName}
                    onChange={(e) => setFormData({ ...formData, adminName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {editingCompany ? 'Guardar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
